import { useMemo } from 'react';
import { computeViewAnchoredRecurrenceRange, expandRecurringEvents } from '../recurrence';
import type { CalendarTimeFilter } from '../eventFetchWindow';
import {
  parseContactFilterId,
  buildCalendarIdToAccountId,
  computeHideXaviorEvents,
  filterCalendarEvents,
  computeModuleSyncError,
  computeConnectedEmptyVariant,
  dedupeCrossAccountEvents,
  type ConnectedEmptyVariant,
  type ModuleSyncError,
} from '../lib/calendarEventFilters';
import type { CalendarAccount } from './useCalendarSync';

/**
 * Event-data derivation pipeline for EventListModule (#3279/#3277): the useMemo
 * cluster that turns the raw `allEvents` query + calendar-account state into the
 * rendered event list and empty-state diagnosis.
 *
 * INVARIANTS:
 * - Dependency arrays are byte-for-byte the same as the original inline memos —
 *   including the now-inert `moduleId`/`filterText` entries the `events` memo
 *   carries — so memo-identity churn is identical to before.
 * - `expandedEvents` expands with a VIEW-ANCHORED window (issue #4298): the
 *   default now −3mo/+12mo range unioned with the padded bounds of the navigated
 *   view (`calendarDate`/`timeFilter`), so paging >12mo out still generates the
 *   on-screen occurrences. `calendarDate`/`timeFilter` are in the dep array so
 *   navigation re-expands.
 * - Called ONCE in the orchestrator, after the Google-sync hook + `allEvents`
 *   are available, so hook order/count is fixed.
 */
export interface UseCalendarEventDataOptions {
  allEvents: any[];
  filterText?: string | null;
  moduleId: string;
  calendarScope: string;
  calendarAccounts: CalendarAccount[];
  accountCalendars: Record<string, { calendars: Array<{ id: string; name: string; primary: boolean; accessRole: string; color: string | null; selected: boolean }>; loading: boolean; error: string | null }>;
  recentlyUncheckedCalendarIds: Set<string>;
  /**
   * The calendar's currently-navigated anchor date. Threaded so the recurrence
   * expander can cover the viewed range when the user pages far from today
   * (issue #4298). Optional: omitting it falls back to the default now-window.
   */
  calendarDate?: Date | null;
  /** The active view (today/week/month), paired with `calendarDate` (#4298). */
  timeFilter?: CalendarTimeFilter | null;
}

export interface UseCalendarEventDataResult {
  events: any[];
  expandedEvents: any[];
  moduleSyncError: ModuleSyncError | null;
  connectedEmptyVariant: ConnectedEmptyVariant;
}

export function useCalendarEventData(opts: UseCalendarEventDataOptions): UseCalendarEventDataResult {
  const {
    allEvents,
    filterText,
    moduleId,
    calendarScope,
    calendarAccounts,
    accountCalendars,
    recentlyUncheckedCalendarIds,
    calendarDate,
    timeFilter,
  } = opts;

  const contactFilterId = useMemo(
    () => parseContactFilterId(filterText),
    [filterText],
  );

  // #2726: calendarId → accountId map for scope-filtering.
  const calendarIdToAccountId = useMemo(
    () => buildCalendarIdToAccountId(accountCalendars),
    [accountCalendars],
  );

  // #2724: gate Xavior events on the first-enabled account's primary calendar.
  const hideXaviorEvents = useMemo(
    () => computeHideXaviorEvents(calendarAccounts, accountCalendars),
    [calendarAccounts, accountCalendars],
  );

  // Events are already filtered by moduleId on the backend via module_links JOIN.
  const events = useMemo(
    () => filterCalendarEvents({
      allEvents,
      hideXaviorEvents,
      calendarScope,
      calendarAccounts,
      calendarIdToAccountId,
      accountCalendars,
      recentlyUncheckedCalendarIds,
      contactFilterId,
      filterText,
    }),
    // NOTE: deps mirror the original inline `events` memo, including the inert
    // `moduleId`/`filterText` entries it carried, so memo-recompute cadence is
    // unchanged. `accountCalendars` is added for the #5564 account-specific
    // scope-readiness gate; it changes in lockstep with `calendarIdToAccountId`
    // (which is derived from it and already a dep), so recompute cadence is
    // identical — the entry is added for correctness, not to widen it. (The
    // repo's eslint config does not register react-hooks/exhaustive-deps,
    // matching the sibling calendar hooks.)
    [allEvents, contactFilterId, filterText, moduleId, recentlyUncheckedCalendarIds, hideXaviorEvents, calendarScope, calendarIdToAccountId, calendarAccounts, accountCalendars],
  );

  // Expand recurring events into virtual instances for the VIEW-ANCHORED range
  // (#4298): the default now −3mo/+12mo window unioned with the padded bounds of
  // the navigated view, so paging >12mo out still generates the on-screen
  // occurrences instead of the series appearing to "stop" after ~1 year. Near
  // today the default window dominates, so current-window rendering is unchanged.
  // Expand first, THEN collapse cross-account duplicates (the same event synced
  // from two connected accounts renders twice). Dedup runs post-expansion so
  // recurring copies anchored on different dates per account merge on the shared
  // occurrence, while distinct occurrences of one series stay separate.
  const expandedEvents = useMemo(
    () => dedupeCrossAccountEvents(
      expandRecurringEvents(
        events,
        computeViewAnchoredRecurrenceRange(new Date(), calendarDate, timeFilter),
      ),
    ),
    [events, calendarDate, timeFilter],
  );

  // Module-wide sync error: only when EVERY enabled account is in the same state.
  const moduleSyncError = useMemo(
    () => computeModuleSyncError(calendarAccounts),
    [calendarAccounts],
  );

  // Connected-but-empty surface discriminator.
  const connectedEmptyVariant = useMemo<ConnectedEmptyVariant>(
    () => computeConnectedEmptyVariant(moduleSyncError, calendarAccounts, accountCalendars),
    [moduleSyncError, calendarAccounts, accountCalendars],
  );

  return { events, expandedEvents, moduleSyncError, connectedEmptyVariant };
}
